import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell';
import { ReportMarkdown } from '../../components/ReportMarkdown';
import { Figure } from '../../components/course/CoursePictures';
import { InlineText } from '../../components/course/InlineText';
import { CourseApiError, isTooOften, useExam, useSubmitExam, useSaveExamSelfCheck, useExamSelfCheck, useDecideExamSelfCheck } from '../../hooks/useCourse';
import { useLessonTracking } from '../../hooks/useLessonTracking';
import { ExamSelfCheck } from '../../components/course/ExamSelfCheck';
import { useAuth } from '../../store/auth';
import { chatSessionUserId } from '../../lib/chat-sync';
import { courseChatHref } from '../../lib/chat-links';
import type { ExamSelfCheckDto, SelfMark } from '../../types/course';
import { isFirstHistoryEntry } from '../../lib/history-back';
import styles from './ExamPage.module.css';

const MAX_ANSWER = 5000;

/**
 * Block exam. Every field is rendered explicitly (never spread from the DTO), so anything the server
 * should not send — a solution, a "critical error" mark — can never reach the DOM.
 */
export function ExamPage(): JSX.Element {
  const { code = '' } = useParams();
  const { t } = useTranslation('course');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  // Exams open only from the course page, so «to course» is a step back (no second /course entry in history).
  const backToCourse = (): void => { if (isFirstHistoryEntry()) navigate('/course', { replace: true }); else navigate(-1); };
  const examQ = useExam(code);
  const submit = useSubmitExam(code);
  const saveSelf = useSaveExamSelfCheck(code);
  const decideSelf = useDecideExamSelfCheck(code);
  const [params, setParams] = useSearchParams();
  const selfQ = useExamSelfCheck(code, params.get('selfCheck'));
  const [snapshot, setSnapshot] = useState<ExamSelfCheckDto | null>(null);
  const self = snapshot ?? selfQ.data;
  const busy = useRef(false);
  const saveRequest = useRef<{ fingerprint: string; id: string } | null>(null);
  const decisionRequest = useRef<{ id: string; mark: SelfMark } | null>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);
  const jwt = useAuth((s) => s.jwt);
  const knownUserId = useAuth((s) => s.user?.id);
  const userId = chatSessionUserId(jwt, knownUserId);
  const draftKey = userId ? `course-exam-draft:${userId}:${code}` : null;
  const [drafts, setDrafts] = useState<Record<number, string>>(() => {
    try {
      const stored = draftKey ? JSON.parse(sessionStorage.getItem(draftKey) ?? '{}') as Record<string, unknown> : {};
      return Object.fromEntries(Object.entries(stored).filter(([key, value]) => /^[1-9]\d*$/.test(key) && typeof value === 'string' && value.length <= MAX_ANSWER)) as Record<number, string>;
    } catch { return {}; }
  });
  useEffect(() => {
    if (!draftKey) return;
    try { sessionStorage.setItem(draftKey, JSON.stringify(drafts)); } catch { /* Drafts remain in memory. */ }
  }, [draftKey, drafts]);
  const [error, setError] = useState<unknown>(null);

  const exam = examQ.data;
  useEffect(() => {
    if (!error && !selfQ.error) return;
    errorRef.current?.focus();
    const title = document.title;
    document.title = `${t('exam.error')} — ${title}`;
    return () => { document.title = title; };
  }, [error, selfQ.error, t]);
  const discuss = (number: number, attemptId: string): void => {
    if (!exam) return;
    navigate(courseChatHref({ kind: 'exam_item', examId: exam.examId, targetId: String(number), attemptId }));
  };
  async function handleSelf(): Promise<void> {
    if (!exam || busy.current) return;
    busy.current = true; setError(null);
    const answers = Object.fromEntries(exam.content.items.map((item) => [String(item.number), drafts[item.number] ?? self?.items.find((i) => i.number === item.number)?.answer ?? '']));
    const fingerprint = JSON.stringify({ answers, versionNumber: exam.versionNumber });
    if (saveRequest.current?.fingerprint !== fingerprint) saveRequest.current = { fingerprint, id: crypto.randomUUID() };
    try {
      const result = await saveSelf.mutateAsync({ answers, versionNumber: exam.versionNumber, requestId: saveRequest.current!.id });
      setSnapshot(result); setParams({ selfCheck: result.id }, { replace: true });
    } catch (e) {
      setError(e);
      if (e instanceof CourseApiError && e.status === 409) void examQ.refetch();
    } finally { busy.current = false; }
  }
  async function handleDecision(mark: SelfMark): Promise<void> {
    if (!self || busy.current || self.completedAttempt) return;
    busy.current = true; setError(null);
    if (!decisionRequest.current) decisionRequest.current = { id: crypto.randomUUID(), mark };
    try {
      const result = await decideSelf.mutateAsync({ id: self.id, mark: decisionRequest.current.mark, requestId: decisionRequest.current.id });
      setSnapshot({ ...self, completedAttempt: result });
    } catch (e) { setError(e); if (e instanceof CourseApiError && e.status === 409) void examQ.refetch(); }
    finally { busy.current = false; }
  }
  function retrySelf(): void {
    if (self) setDrafts(Object.fromEntries(self.items.map((i) => [i.number, i.answer])));
    setSnapshot(null); setParams({}, { replace: true }); saveRequest.current = null; decisionRequest.current = null; setError(null);
  }

  // One `open` event per visit (no scroll milestones for an exam); never disturbs the page.
  const bodyRef = useRef<HTMLDivElement>(null);
  useLessonTracking({ kind: 'exam', code, versionNumber: exam?.versionNumber, contentRef: bodyRef, enabled: !!exam });

  async function handleSubmit(numbers: number[]): Promise<void> {
    if (busy.current) return;
    busy.current = true;
    setError(null);
    const answers: Record<string, string> = {};
    for (const n of numbers) answers[String(n)] = drafts[n] ?? '';
    try {
      const res = await submit.mutateAsync({ answers });
      // Sent. If the check itself failed the learner sends the same answers again, so they stay.
      if (res?.attempts?.at(-1)?.status !== 'failed') setDrafts({});
    } catch (e) {
      if (e instanceof CourseApiError && e.status === 429 && e.code === 'attempts_exhausted') {
        // The page was stale (a pending attempt or the day's attempts used up): reload it to show the reason.
        void examQ.refetch();
        return;
      }
      if (e instanceof CourseApiError && e.code === 'already_passed') void examQ.refetch();
      setError(e);
    } finally { busy.current = false; }
  }

  const toCourse = (
    <Link to="/course" onClick={(e) => { e.preventDefault(); backToCourse(); }} className={styles.link} data-testid="exam-to-course">{t('lesson.toCourse')} ›</Link>
  );

  const errCode = examQ.error instanceof CourseApiError ? examQ.error.code : null;
  // Banner over an exam that is already on screen: say why the refresh failed when the server told us.
  const refreshKey = errCode === 'locked' ? 'exam.refreshLocked' : errCode === 'not_launched' ? 'refreshNotLaunched' : 'exam.refreshFailed';
  const KNOWN: Record<string, { key: string; testId: string }> = {
    locked: { key: 'exam.locked', testId: 'exam-locked' },
    not_enrolled: { key: 'lesson.notEnrolled', testId: 'exam-not-enrolled' },
    not_launched: { key: 'notLaunched', testId: 'exam-not-launched' },
  };

  let body: JSX.Element;
  if (!exam) {
    // Nothing to show yet. Once the exam is on screen, a failed refetch never replaces it (see the banner below).
    const known = errCode ? KNOWN[errCode] : undefined;
    if (examQ.isLoading || (examQ.isPending && !examQ.isError)) {
      body = <p data-testid="exam-loading">{tc('states.loading')}</p>;
    } else if (known) {
      body = (
        <>
          <p role="alert" data-testid={known.testId}>{t(known.key)}</p>
          {toCourse}
        </>
      );
    } else {
      body = <p role="alert" data-testid="exam-error-page">{tc('errors.generic')}</p>;
    }
  } else {
    const c = exam.content;
    const numbers = c.items.map((i) => i.number);
    const attempts = [...exam.attempts].reverse(); // newest first
    const pending = exam.attempts.some((a) => a.status === 'pending');
    const lastGraded = attempts.find((a) => a.status === 'graded' && a.gradingMethod !== 'self');
    const latestGraded = attempts[0]?.status === 'graded' && attempts[0]?.gradingMethod !== 'self' ? attempts[0] : undefined;
    const lastFailed = attempts[0]?.status === 'failed' && !pending && !exam.passed;
    const feedbackFor = (n: number): { points: number; comment: string } | undefined =>
      lastGraded?.feedback?.find((f) => f.number === n);
    const noAttempts = exam.attemptsLeftToday <= 0;
    const aiPassed = attempts.some((a) => a.gradingMethod !== 'self' && a.passed);
    const showForm = !pending && !aiPassed && !self && !params.get('selfCheck');
    const allFilled = c.items.every((i) => (drafts[i.number] ?? '').trim() !== '');
    const errText = isTooOften(error)
      ? t('lesson.tooOften')
      : error instanceof CourseApiError && error.code === 'already_passed' ? t('exam.alreadyPassed') : t('exam.error');

    body = (
      <>
        {examQ.isError && (
          <p role="alert" className={styles.banner} data-testid="exam-refresh-failed">{t(refreshKey)}</p>
        )}
        {exam.draft && <p className={styles.banner} data-testid="exam-draft">{t('lesson.draftBanner')}</p>}
        <h2 className={styles.title}><InlineText text={c.title} /></h2>
        {c.intro && <p className={styles.subtitle}><InlineText text={c.intro} /></p>}
        {c.reference && (
          <div className={styles.reference} data-testid="exam-reference">
            <ReportMarkdown markdown={c.reference} />
          </div>
        )}

        {c.items.map((item) => {
          const fb = feedbackFor(item.number);
          const completed = attempts.find((a) => a.status === 'graded' && typeof a.passed === 'boolean'
            && (a.gradingMethod === 'ai' || a.gradingMethod === 'self') && a.versionNumber === exam.versionNumber);
          return (
            <section key={item.number} className={styles.item} data-testid={`exam-item-${item.number}`}>
              <h3 className={styles.section}>{item.number}. <InlineText text={item.title} /></h3>
              <p className={styles.prompt}><InlineText text={item.prompt} /></p>
              {item.image && <Figure image={item.image} caption="" />}
              {showForm && (
                <textarea
                  className={styles.textarea}
                  rows={4}
                  maxLength={MAX_ANSWER}
                  value={drafts[item.number] ?? ''}
                  aria-label={t('exam.yourAnswer')}
                  onChange={(e) => setDrafts((d) => ({ ...d, [item.number]: e.target.value }))}
                  data-testid={`exam-answer-${item.number}`}
                />
              )}
              {completed && !self && <button type="button" className={styles.secondary} onClick={() => discuss(item.number, completed.id)} data-testid={`exam-discuss-${item.number}`}>{t('chat.discuss')}</button>}
              {fb && (
                <>
                  {/* The comments are written by a model, never by a person: the learner is told so. */}
                  <h4 className={styles.aiLabel} data-testid={`exam-ai-label-${item.number}`}>{t('exam.aiCheck')}</h4>
                  <p className={styles.feedback} data-testid={`exam-feedback-${item.number}`}>
                    {t('exam.points', { points: fb.points, max: c.pointsPerItem })}{fb.comment ? ` — ${fb.comment}` : ''}
                  </p>
                </>
              )}
            </section>
          );
        })}

        {selfQ.isLoading && params.get('selfCheck') && <p role="status">{tc('states.loading')}</p>}
        {selfQ.error && <p ref={errorRef} tabIndex={-1} role="alert" className={styles.error}>{selfQ.error instanceof CourseApiError && selfQ.error.code === 'version_conflict' ? t('exam.versionChanged') : t('exam.saveError')} <button type="button" className={styles.secondary} onClick={() => void selfQ.refetch()}>{t('lesson.retry')}</button> <button type="button" className={styles.secondary} onClick={retrySelf} data-testid="self-retry">{t('exam.retrySelf')}</button></p>}
        {self && <ExamSelfCheck snapshot={self} busy={decideSelf.isPending} pendingMark={decisionRequest.current?.mark} onMark={(mark) => void handleDecision(mark)} onRetry={retrySelf} onDiscuss={discuss} error={error ? error instanceof CourseApiError && error.code === 'version_conflict' ? t('exam.versionChanged') : t('exam.decisionError') : null} />}
        {pending && <p className={styles.notice} data-testid="exam-pending">{t('exam.pending')}</p>}
        {latestGraded && (
          <p className={latestGraded.passed ? styles.success : styles.notice} data-testid="exam-result">
            {latestGraded.passed ? t('exam.resultPassed') : t('exam.resultNotPassed')}
          </p>
        )}
        {lastFailed && showForm && <p className={styles.notice} data-testid="exam-failed">{t('exam.failed')}</p>}
        {exam.passed && (
          <div data-testid="exam-passed">
            <p className={styles.success}>{t('exam.passed')}</p>
            <button type="button" className={styles.primary} onClick={backToCourse} data-testid="exam-to-course-btn">
              {t('lesson.toCourse')}
            </button>
          </div>
        )}
        {!exam.passed && !pending && noAttempts && <p className={styles.notice} data-testid="exam-exhausted">{t('exam.exhausted')}</p>}

        {error !== null && !self && <p ref={errorRef} tabIndex={-1} role="alert" className={styles.error} data-testid="exam-error">{error instanceof CourseApiError && error.code === 'version_conflict' ? t('exam.versionChanged') : errText}</p>}
        {showForm && (
          <div className={styles.actions}>
          <button type="button" className={styles.primary} disabled={!allFilled || saveSelf.isPending || submit.isPending} onClick={() => void handleSelf()} data-testid="exam-self-submit">{saveSelf.isPending ? t('exam.saving') : t('exam.selfCheck')}</button>
          <button
            type="button"
            className={styles.primary}
            disabled={!allFilled || submit.isPending || saveSelf.isPending || noAttempts}
            onClick={() => void handleSubmit(numbers)}
            data-testid="exam-submit"
          >
            {submit.isPending ? t('exam.sending') : t('exam.methodAi')}
          </button>
          </div>
        )}

        {attempts.some((a) => a.status === 'graded') && (
          <>
            <h3 className={styles.section}>{t('exam.attempts')}</h3>
            <ul className={styles.attempts}>
              {attempts.filter((a) => a.status === 'graded').map((a) => (
                <li key={a.id} data-testid={`exam-score-${a.id}`}>
                  {a.gradingMethod === 'self' ? t('exam.methodSelf') : `${t('exam.methodAi')} · ${t('exam.score', { score: a.score ?? 0, max: a.maxScore ?? 0 })}`} · {a.passed ? t('exam.ok') : t('exam.notOk')}
                </li>
              ))}
            </ul>
          </>
        )}
        {!exam.passed && toCourse}
      </>
    );
  }

  return (
    <MeSubpageShell title={t('pageTitle')} onBack={backToCourse} data-testid="exam-page">
      <div className={styles.body} ref={bodyRef}>{body}</div>
    </MeSubpageShell>
  );
}
